/* eslint-disable no-underscore-dangle, max-len */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminImages, updateAdminImage, deleteAdminImage, getImageRatings,
} from '../../services/adminApi';
import ImageUploadModal from '../../components/admin/ImageUploadModal';
import ManageSchedulesModal from '../../components/admin/ManageSchedulesModal';
import './admin.css';

function ImageLibrary() {
  const [images, setImages] = useState([]);
  const [imageRatings, setImageRatings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [managingImage, setManagingImage] = useState(null);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      const data = await getAdminImages();
      setImages(data);

      // Fetch ratings for images that have been used
      const ratingsPromises = data
        .filter((img) => img.usageCount > 0)
        .map(async (img) => {
          try {
            const ratings = await getImageRatings(img._id);
            return { imageId: img._id, ratings };
          } catch {
            return { imageId: img._id, ratings: null };
          }
        });

      const ratingsResults = await Promise.all(ratingsPromises);
      const ratingsMap = {};
      ratingsResults.forEach(({ imageId, ratings }) => {
        if (ratings) {
          ratingsMap[imageId] = ratings;
        }
      });
      setImageRatings(ratingsMap);

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const handleToggleActive = async (imageId, currentActive) => {
    try {
      setTogglingId(imageId);
      await updateAdminImage(imageId, { isActive: !currentActive });
      setImages((prev) => prev.map((img) => (img._id === imageId ? { ...img, isActive: !currentActive } : img)));
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (imageId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await deleteAdminImage(imageId);
      setImages((prev) => prev.filter((img) => img._id !== imageId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleScheduleChange = async () => {
    await loadImages();
    // Update managingImage with fresh data
    if (managingImage) {
      const freshImages = await getAdminImages();
      const updated = freshImages.find((img) => img._id === managingImage._id);
      if (updated) setManagingImage(updated);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-puzzle-text">Image Library</h1>
            <p className="text-puzzle-text-secondary mt-1">Manage images for daily puzzles</p>
          </div>
          <div className="flex gap-4">
            <Link
              to="/admin/stats"
              className="px-4 py-2 bg-puzzle-surface text-puzzle-text-secondary rounded-lg hover:bg-puzzle-hover transition-colors border border-puzzle-border"
            >
              Analytics
            </Link>
            <Link
              to="/admin/daily-puzzles"
              className="px-4 py-2 bg-puzzle-surface text-puzzle-text-secondary rounded-lg hover:bg-puzzle-hover transition-colors border border-puzzle-border"
            >
              Daily Puzzles
            </Link>
            <Link
              to="/admin/feedback"
              className="px-4 py-2 bg-puzzle-surface text-puzzle-text-secondary rounded-lg hover:bg-puzzle-hover transition-colors border border-puzzle-border"
            >
              Feedback
            </Link>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Upload Image
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg text-puzzle-danger">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-4 text-puzzle-danger/70 hover:text-puzzle-danger"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Image Grid */}
        {images.length === 0 ? (
          <div className="text-center py-12 bg-puzzle-surface rounded-lg border border-puzzle-border">
            <svg className="mx-auto h-12 w-12 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-puzzle-text">No images yet</h3>
            <p className="mt-1 text-sm text-puzzle-text-secondary">Upload images to use for daily puzzles.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <div
                key={image._id}
                className={`bg-puzzle-surface rounded-lg border overflow-hidden shadow-sm ${
                  image.isActive ? 'border-puzzle-green/30' : 'border-puzzle-border'
                }`}
              >
                <div className="aspect-video bg-puzzle-muted relative">
                  {image.thumbnailData ? (
                    <img
                      src={image.thumbnailData}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  {!image.isActive && (
                    <div className="absolute inset-0 bg-puzzle-text/50 flex items-center justify-center">
                      <span className="text-white font-medium">Inactive</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-puzzle-text truncate">{image.title}</h3>
                  {image.description && (
                    <p className="text-sm text-puzzle-text-secondary mt-1 line-clamp-2">{image.description}</p>
                  )}
                  {image.categories && image.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {image.categories.map((cat) => (
                        <span
                          key={cat}
                          className="px-2 py-0.5 bg-puzzle-background text-puzzle-text-secondary text-xs rounded"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3">
                    <span className="text-xs text-puzzle-text-secondary">
                      Used
                      {' '}
                      {image.usageCount}
                      {' '}
                      times
                    </span>
                    {image.scheduledDates && image.scheduledDates.length > 0 && (
                      <div className="text-xs text-puzzle-green mt-1">
                        Scheduled:
                        {' '}
                        {image.scheduledDates
                          .map((s) => new Date(s.date).toLocaleDateString('en-US', { timeZone: 'UTC' }))
                          .join(', ')}
                      </div>
                    )}
                    {imageRatings[image._id] && imageRatings[image._id].total > 0 && (
                      <div className="text-xs text-puzzle-text-secondary mt-1">
                        <span title="Loved it">
                          {'😊 '}
                          {imageRatings[image._id].happy}
                        </span>
                        {' · '}
                        <span title="It was okay">
                          {'😐 '}
                          {imageRatings[image._id].neutral}
                        </span>
                        {' · '}
                        <span title="Not great">
                          {'😞 '}
                          {imageRatings[image._id].sad}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(image._id, image.isActive)}
                        disabled={togglingId === image._id}
                        className={`px-3 py-1 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                          image.isActive
                            ? 'bg-puzzle-warning/10 text-puzzle-warning hover:bg-puzzle-warning/20'
                            : 'bg-puzzle-success/10 text-puzzle-success hover:bg-puzzle-success/20'
                        }`}
                      >
                        {(() => {
                          if (togglingId === image._id) {
                            return (
                              <span className="flex items-center gap-1">
                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                {image.isActive ? 'Deactivating...' : 'Activating...'}
                              </span>
                            );
                          }
                          return image.isActive ? 'Deactivate' : 'Activate';
                        })()}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(image._id)}
                        className="px-3 py-1 text-sm bg-puzzle-danger/10 text-puzzle-danger rounded hover:bg-puzzle-danger/20"
                      >
                        Delete
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setManagingImage(image)}
                      className="px-3 py-1 text-sm bg-puzzle-blue/10 text-puzzle-blue rounded hover:bg-puzzle-blue/20"
                    >
                      Schedules
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Manage Schedules Modal */}
        <ManageSchedulesModal
          image={managingImage}
          onClose={() => setManagingImage(null)}
          onScheduleChange={handleScheduleChange}
          onError={(msg) => setError(msg)}
        />

        {/* Upload Modal */}
        <ImageUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={loadImages}
        />
      </div>
    </div>
  );
}

export default ImageLibrary;
